
import { Button } from "@/components/ui/button";
import { useCountry } from "@/contexts/country";
import { MapPin, Globe, TrendingUp } from "lucide-react";
import { useGeolocation } from "@/components/pollster/hooks/useGeolocation";
import { Badge } from "@/components/ui/badge";

const ModernHeroSection = () => {
  const { countryCode, countryName } = useCountry();
  const { geoLocation, isLoading } = useGeolocation();

  const detectedCountryCode = geoLocation?.countryCode || countryCode;
  const detectedCountryName =
    geoLocation?.countryName || countryName || "your region";

  const handleScrollToSectors = () => {
    const element = document.querySelector('#sectors');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative pt-24 pb-20 md:pt-32 md:pb-28 overflow-hidden bg-gradient-to-br from-nation-blue via-nation-blue-dark to-nation-purple">
      {/* Background overlay with existing hero image */}
      <div className="absolute inset-0 opacity-20 bg-[url('/img/hero-26-1920x480.png')] bg-cover bg-center"></div>
      <div className="absolute inset-0 bg-gradient-to-br from-nation-blue/90 via-nation-blue-dark/85 to-nation-purple/90"></div>
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Column - Main Content */}
            <div className="text-white">
              {/* Geolocation Detection */}
              {!isLoading && detectedCountryCode && (
                <div className="mb-6 inline-flex items-center">
                  <Badge
                    variant="outline"
                    className="bg-white/10 text-white border-white/30 px-4 py-2 text-sm backdrop-blur-sm"
                  >
                    <MapPin size={16} className="mr-2" />
                    <span>Detecting insights from {detectedCountryName}</span>
                  </Badge>
                </div>
              )}

              {/* Main Headline */}
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                NationCard™: Real-Time Public Sentiment & 
                <span className="text-nation-teal"> Verified Research Data</span>
              </h1>

              {/* Subheadline */}
              <p className="text-xl md:text-2xl mb-8 opacity-90 leading-relaxed">
                Empower your organization with live citizen insights—no more guesswork, no more delays.
              </p>

              {/* Primary CTA */}
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <Button 
                  size="lg" 
                  onClick={handleScrollToSectors}
                  className="bg-nation-teal hover:bg-nation-teal-dark text-white px-8 py-4 text-lg h-auto font-semibold shadow-xl hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300"
                >
                  Choose Your Sector
                </Button>
                
                <Button 
                  size="lg" 
                  variant="outline"
                  className="border-white/30 text-white hover:bg-white/10 px-8 py-4 text-lg h-auto backdrop-blur-sm"
                  onClick={() => {
                    const element = document.querySelector('#how-it-works');
                    if (element) element.scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  See How It Works
                </Button>
              </div>

              {/* Trust Indicators */}
              <div className="flex flex-wrap items-center gap-4 text-sm opacity-80">
                <div className="flex items-center gap-2">
                  <Globe size={16} />
                  <span>100+ Countries</span>
                </div>
                <div className="flex items-center gap-2">
                  <TrendingUp size={16} />
                  <span>Real-time Analytics</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                  <span>Live Data Stream</span>
                </div>
              </div>
            </div>

            {/* Right Column - Visual Dashboard Preview */}
            <div className="hidden lg:block relative">
              <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20 shadow-2xl">
                <div className="space-y-4">
                  {/* Mock Dashboard Header */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-nation-teal rounded-lg flex items-center justify-center">
                        <TrendingUp size={16} className="text-white" />
                      </div>
                      <div>
                        <div className="text-white font-semibold">Live Sentiment</div>
                        <div className="text-white/60 text-sm">Global Overview</div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
                      <div className="w-3 h-3 bg-blue-400 rounded-full animate-pulse delay-150"></div>
                      <div className="w-3 h-3 bg-purple-400 rounded-full animate-pulse delay-300"></div>
                    </div>
                  </div>

                  {/* Mock Charts */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-white/5 rounded-lg p-4">
                      <div className="text-white/80 text-xs mb-2">Positive Sentiment</div>
                      <div className="text-green-400 text-2xl font-bold">68%</div>
                      <div className="w-full bg-white/10 rounded-full h-2 mt-2">
                        <div className="bg-green-400 h-2 rounded-full w-2/3"></div>
                      </div>
                    </div>
                    <div className="bg-white/5 rounded-lg p-4">
                      <div className="text-white/80 text-xs mb-2">Active Panels</div>
                      <div className="text-nation-teal text-2xl font-bold">2.4M</div>
                      <div className="text-white/60 text-xs mt-1">+12% this week</div>
                    </div>
                  </div>

                  {/* Activity Feed */}
                  <div className="space-y-2">
                    <div className="text-white/80 text-xs">Recent Activity</div>
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 text-xs text-white/70">
                        <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                        <span>UK: Healthcare sentiment +15%</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-white/70">
                        <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                        <span>US: Economic confidence -3%</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-white/70">
                        <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                        <span>CA: Environmental alerts +8%</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating cards */}
              <div className="absolute -top-4 -right-4 bg-white/10 backdrop-blur-sm rounded-lg p-3 border border-white/20">
                <div className="text-white text-xs">Verified Data</div>
                <div className="text-nation-teal font-bold">99.7%</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ModernHeroSection;
